import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@tcg/ui";
import { ArrowLeft, ArrowRight, CheckSquare, Scissors, Stamp, WifiOff } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { usePos } from "@/app/context";
import { Modal } from "@/components/Modal";
import { MoneyText } from "@/components/MoneyText";
import { useToast } from "@/components/Toast";
import { cartKey } from "@/features/sale/CartContext";
import { useCart } from "@/features/sale/hooks/useCart";
import type { TenderEntryState } from "@/features/tender/TenderScreen";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { call } from "@/lib/api";
import { errorText, isApiError, type ApiError } from "@/lib/errors";
import { newIdempotencyKey } from "@/lib/idempotency";
import type { CartLine, TaxFreeVerification } from "@/lib/types";

import { EligibilityResult } from "./EligibilityResult";
import { defaultDetail, HighValueDetail, type LineDetail } from "./HighValueDetail";
import type { BuyerLocale } from "./notices";
import { PassportForm } from "./PassportForm";
import { emptyPassport, toVerifyBody, type PassportDraft, type PassportErrors } from "./passport";
import { RecordsList } from "./RecordsList";
import { offeredRefundMethods, RefundMethodForm, type TaxRefundMethod } from "./RefundMethodForm";
import { RegimeCard } from "./RegimeCard";

type Step = 1 | 2 | 3 | 4 | 5;

const OFFLINE = { "X-Pos-Offline": "false" } as const;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * 🛂 免税 (T07-30, design/05 §3.3) — five steps on one screen, the system decides:
 * (1) パスポート → tax-service checks it against this basket; (2) 結果 ✅ / ❌ with the reasons;
 * (3) 方式 — the regime of today (免税価格 until 2026-10-31 / リファンド方式 from 2026-11-01) with
 * what to tell the buyer in the buyer's language, 商品情報詳細 for ≥ ¥1,000,000; (4) リファンド
 *方式: how the tax goes back + the buyer's e-mail; (5) 「免税で会計へ進む」 → お会計 with the
 * new total (the receipt always prints). Online only.
 */
export function TaxFreeWizard() {
  const { t } = useTranslation();
  const { api } = usePos();
  const cart = useCart();
  const online = useOnlineStatus();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>(1);
  const [tab, setTab] = useState<"sale" | "records">("sale");
  const [passport, setPassport] = useState<PassportDraft>(emptyPassport);
  const [errors, setErrors] = useState<PassportErrors>({});
  const [result, setResult] = useState<TaxFreeVerification | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [buyerLocale, setBuyerLocale] = useState<BuyerLocale>("en");
  const [explained, setExplained] = useState(false);
  const [details, setDetails] = useState<Record<string, LineDetail>>({});
  const [refundMethod, setRefundMethod] = useState<TaxRefundMethod | null>(null);
  const [email, setEmail] = useState("");
  const [splitAsk, setSplitAsk] = useState(false);
  const verifyKey = useRef(newIdempotencyKey());
  const basket = cart.cart;
  const cartId = cart.cartId;

  const status = useQuery({
    queryKey: ["pos-carts", "detail", cartId, "tax-free"],
    queryFn: () =>
      call(() =>
        api.GET("/v1/pos/carts/{cart_id}/tax-free", {
          params: { path: { cart_id: cartId ?? "" } },
        }),
      ),
    enabled: Boolean(cartId) && online,
  });
  const methods = offeredRefundMethods(status.data?.refundMethods ?? []);
  const regime = result?.regime ?? status.data?.regime ?? null;
  const highValueLines = useMemo<CartLine[]>(
    () => (basket?.lines ?? []).filter((l) => result?.requiresDetailLineIds?.includes(l.id)),
    [basket, result],
  );

  if (!online) {
    return (
      <section className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <WifiOff aria-hidden className="size-14 text-pos-problem" />
        <h1 className="text-2xl font-semibold">{t("taxfree.title")}</h1>
        <p role="alert" className="text-xl">
          {t("taxfree.online_only")}
        </p>
      </section>
    );
  }
  if (!basket || basket.lines.length === 0) {
    return (
      <section className="flex h-full flex-col gap-4 p-6">
        <h1 className="text-2xl font-semibold">{t("taxfree.title")}</h1>
        <p className="text-lg text-muted-foreground">{t("taxfree.basket_empty")}</p>
        <RecordsList />
      </section>
    );
  }

  const verify = async () => {
    if (!cartId) {
      return;
    }
    const { body, errors: found } = toVerifyBody(passport);
    setErrors(found);
    if (!body) {
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const answer = await call(() =>
        api.POST("/v1/pos/carts/{cart_id}/tax-free/verify", {
          params: { path: { cart_id: cartId } },
          body,
          headers: { ...OFFLINE, "Idempotency-Key": verifyKey.current },
        }),
      );
      verifyKey.current = newIdempotencyKey();
      setResult(answer);
      setVerificationId(answer.eligible ? answer.verificationId : null);
      setStep(2);
      if (answer.eligible && answer.suggestSeparateReceipt) {
        setSplitAsk(true);
      }
    } catch (err) {
      verifyKey.current = newIdempotencyKey();
      setProblem(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  /** 「サプライ品は別会計にしますか？」 — the lines that cannot be 免税 go to a parked basket. */
  const split = async () => {
    if (!result || !cartId) {
      return;
    }
    setBusy(true);
    try {
      const ids = new Set(result.ineligibleLineIds ?? []);
      const moving = basket.lines.filter((l) => ids.has(l.id));
      const other = await call(() => api.POST("/v1/pos/carts", { body: {} }));
      for (const line of moving) {
        await call(() =>
          api.DELETE("/v1/pos/carts/{cart_id}/lines/{line_id}", {
            params: { path: { cart_id: cartId, line_id: line.id } },
          }),
        );
        if (line.variantId) {
          await call(() =>
            api.POST("/v1/pos/carts/{cart_id}/lines", {
              params: { path: { cart_id: other.id } },
              body: {
                variantId: line.variantId ?? undefined,
                ...(line.unitId ? { unitId: line.unitId } : {}),
                qty: line.qty,
              },
            }),
          );
        }
      }
      await call(() =>
        api.POST("/v1/pos/carts/{cart_id}/hold", {
          params: { path: { cart_id: other.id } },
          body: { kind: "park", note: t("taxfree.split_note") },
        }),
      );
      await queryClient.invalidateQueries({ queryKey: cartKey(cartId) });
      await queryClient.invalidateQueries({ queryKey: ["pos-holds"] });
      toast.show(t("taxfree.split_done"), "success");
      setSplitAsk(false);
      // the basket changed: check the passport against it again
      setResult(null);
      setVerificationId(null);
      setStep(1);
    } catch (err) {
      toast.show(errorText(err, t), "error");
    } finally {
      setBusy(false);
    }
  };

  const attach = async () => {
    if (!cartId || !verificationId) {
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const lineDetails = highValueLines.map((line) => {
        const detail = details[line.id] ?? defaultDetail(line);
        return { lineId: line.id, itemDetail: detail.itemDetail, serial: detail.serial };
      });
      const next = await call(() =>
        api.POST("/v1/pos/carts/{cart_id}/tax-free", {
          params: {
            path: { cart_id: cartId },
            header: { "If-Match": String(basket.version), ...OFFLINE },
          },
          body: {
            verificationId,
            acknowledged: true,
            ...(lineDetails.length ? { lineDetails } : {}),
            ...(regime === "refund" && refundMethod ? { refundMethod } : {}),
          },
        }),
      );
      queryClient.setQueryData(cartKey(next.id), next);
      const state: TenderEntryState = { taxFree: { locale: buyerLocale, email } };
      void navigate("/pay?method=cash", { state });
    } catch (err) {
      if (isApiError(err, "version_conflict")) {
        cart.refresh();
      }
      if (isApiError(err, "taxfree_mixed_receipt")) {
        setSplitAsk(true);
      }
      if (isApiError(err, "high_value_detail_required")) {
        const missing = (err as ApiError).detail.lineIds;
        if (Array.isArray(missing)) {
          setStep(3);
        }
      }
      setProblem(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const detailsOk = highValueLines.every((l) => {
    const detail = details[l.id] ?? defaultDetail(l);
    return Boolean(detail.serial.trim()) && Boolean(detail.itemDetail.trim());
  });
  const refundOk = regime !== "refund" || (refundMethod !== null && (!email || EMAIL.test(email)));
  const canNext: Record<Step, boolean> = {
    1: !busy,
    2: Boolean(result?.eligible && verificationId),
    3: explained && detailsOk,
    4: refundOk,
    5: Boolean(verificationId) && explained && detailsOk && refundOk && !busy,
  };
  const steps: Array<{ n: Step; label: string }> = [
    { n: 1, label: t("taxfree.step_passport") },
    { n: 2, label: t("taxfree.step_result") },
    { n: 3, label: t("taxfree.step_regime") },
    { n: 4, label: t("taxfree.step_refund") },
    { n: 5, label: t("taxfree.step_pay") },
  ];
  const next = () => {
    if (step === 1) {
      void verify();
    } else if (step === 3 && regime !== "refund") {
      setStep(5);
    } else if (step < 5) {
      setStep((step + 1) as Step);
    }
  };
  const back = () => {
    if (step === 5 && regime !== "refund") {
      setStep(3);
    } else if (step > 1) {
      setStep((step - 1) as Step);
    }
  };

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_1fr_auto] gap-3 p-4">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Stamp aria-hidden />
          {t("taxfree.title")}
        </h1>
        <div role="tablist" className="flex gap-1">
          {(["sale", "records"] as const).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className="min-h-12 rounded-md border px-4 aria-selected:border-primary aria-selected:bg-accent"
            >
              {t(`taxfree.tab_${key}`)}
            </button>
          ))}
        </div>
        {tab === "sale" ? (
          <ol className="ml-auto flex gap-1" aria-label={t("taxfree.steps")}>
            {steps
              .filter((s) => regime === "refund" || s.n !== 4)
              .map((s) => (
                <li
                  key={s.n}
                  aria-current={step === s.n ? "step" : undefined}
                  className={cn(
                    "rounded-full border px-3 py-1 text-sm",
                    step === s.n && "border-primary bg-primary text-primary-foreground",
                    step > s.n && "border-pos-ok text-pos-ok",
                  )}
                >
                  {s.label}
                </li>
              ))}
          </ol>
        ) : null}
      </header>
      {tab === "records" ? (
        <RecordsList />
      ) : (
        <div className="min-h-0 overflow-y-auto">
          {step === 1 ? (
            <PassportForm value={passport} errors={errors} onChange={setPassport} disabled={busy} />
          ) : null}
          {step === 2 && result ? <EligibilityResult result={result} /> : null}
          {step === 3 && result ? (
            <div className="flex flex-col gap-3">
              <RegimeCard
                regime={result.regime}
                exemptionPrice={result.thresholdCheck.currentExclTax}
                total={basket.totals.total}
                refund={basket.tax.amount ?? 0}
                noticeKeys={result.noticeKeys ?? []}
                buyerLocale={buyerLocale}
                onBuyerLocale={setBuyerLocale}
              />
              {highValueLines.length > 0 ? (
                <HighValueDetail
                  lines={highValueLines}
                  details={details}
                  onChange={(id, detail) => setDetails({ ...details, [id]: detail })}
                />
              ) : null}
              <label className="flex min-h-pos-touch items-center gap-3 rounded-md border px-3 text-lg">
                <input
                  type="checkbox"
                  checked={explained}
                  onChange={(e) => setExplained(e.target.checked)}
                  className="size-6"
                />
                <CheckSquare aria-hidden className="size-5" />
                {t("taxfree.explained")}
              </label>
            </div>
          ) : null}
          {step === 4 ? (
            <RefundMethodForm
              methods={methods}
              value={refundMethod}
              onChange={setRefundMethod}
              email={email}
              onEmail={setEmail}
            />
          ) : null}
          {step === 5 && result ? (
            <section className="flex flex-col gap-3 text-lg">
              <p>{t("taxfree.confirm_help")}</p>
              <p className="flex items-baseline gap-3">
                {result.regime === "exemption"
                  ? t("taxfree.exemption_total")
                  : t("taxfree.refund_total")}
                <MoneyText
                  amount={
                    result.regime === "exemption"
                      ? result.thresholdCheck.currentExclTax
                      : basket.totals.total
                  }
                  size="xl"
                />
              </p>
              <p>{t("taxfree.receipt_forced")}</p>
            </section>
          ) : null}
          {problem ? (
            <p
              role="alert"
              data-testid="tf-problem"
              className="mt-3 rounded-md border-2 border-status-problem p-3 text-lg text-pos-problem"
            >
              {problem}
            </p>
          ) : null}
        </div>
      )}
      {tab === "sale" ? (
        <footer className="flex items-center gap-3">
          <button
            type="button"
            onClick={step === 1 ? () => void navigate("/") : back}
            disabled={busy}
            className="flex min-h-pos-touch items-center gap-2 rounded-md border px-5 text-lg"
          >
            <ArrowLeft aria-hidden />
            {step === 1 ? t("taxfree.back_to_sale") : t("app.back")}
          </button>
          <span className="flex-1" />
          {step === 5 ? (
            <button
              type="button"
              onClick={() => void attach()}
              disabled={!canNext[5]}
              className="flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-8 text-xl font-bold text-primary-foreground disabled:opacity-40"
            >
              {busy ? t("app.saving") : t("taxfree.go_pay")}
              <ArrowRight aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              onClick={next}
              disabled={!canNext[step]}
              className="flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-8 text-xl font-semibold text-primary-foreground disabled:opacity-40"
            >
              {busy ? t("app.saving") : step === 1 ? t("taxfree.check") : t("taxfree.next")}
              <ArrowRight aria-hidden />
            </button>
          )}
        </footer>
      ) : null}
      {splitAsk ? (
        <Modal
          open
          title={t("taxfree.split_title")}
          description={t("taxfree.split_help")}
          onClose={() => setSplitAsk(false)}
          busy={busy}
          footer={
            <>
              <button
                type="button"
                onClick={() => setSplitAsk(false)}
                className="min-h-pos-touch rounded-md border px-6 text-lg"
              >
                {t("taxfree.split_no")}
              </button>
              <button
                type="button"
                onClick={() => void split()}
                disabled={busy}
                className="flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground"
              >
                <Scissors aria-hidden />
                {t("taxfree.split_yes")}
              </button>
            </>
          }
        >
          <ul className="flex flex-col gap-1">
            {basket.lines
              .filter((l) => (result?.ineligibleLineIds ?? []).includes(l.id))
              .map((l) => (
                <li key={l.id} className="flex justify-between">
                  <span>{l.title.ja ?? l.title.en}</span>
                  <MoneyText amount={l.lineTotal} />
                </li>
              ))}
          </ul>
        </Modal>
      ) : null}
    </div>
  );
}
